import React from "react";
import useStockChartDataModel from "./stockChartModel";
import ReactApexChart from "react-apexcharts";
import "./StockChart.css";

export default function StockChart({ symbol, name }) {
  const model = useStockChartDataModel();
  const [showSignals, setShowSignals] = React.useState(true);

  React.useEffect(() => {
    if (symbol) {
      model.getStockChartData(symbol);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  return (
    <div className="relative">
      <div className="bg-gray-800/40 rounded-2xl p-6 backdrop-blur-xl border border-gray-700/50 shadow-lg space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-100">
            {name ? `${name} (${symbol})` : "Select a Stock"}
          </h2>
          <button
            onClick={() => setShowSignals(!showSignals)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
              showSignals
                ? "bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/30 hover:bg-blue-500/30"
                : "bg-gray-700/50 text-gray-400 hover:bg-gray-600/50"
            }`}
          >
            {showSignals ? "Hide Signals" : "Show Signals"}
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
          <div className="bg-gray-900/50 rounded-lg p-3 backdrop-blur-sm">
            <div className="text-gray-400 text-sm mb-1">Symbol</div>
            <div className="font-medium text-gray-100">{symbol || "-"}</div>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-3 backdrop-blur-sm">
            <div className="text-gray-400 text-sm mb-1">Status</div>
            <div className="font-medium text-gray-100">
              {model.state.isLoading ? "Loading" : "Ready"}
            </div>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-3 backdrop-blur-sm">
            <div className="text-gray-400 text-sm mb-1">Current Price</div>
            <div className="font-medium text-gray-100">
              {model.state.currentPrice || 0}
            </div>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-3 backdrop-blur-sm">
            <div className="text-gray-400 text-sm mb-1">Signal</div>
            <div
              className={`font-medium rounded-md px-2 py-1 inline-block ${
                model.state.tradingSignal === "BUY"
                  ? "bg-green-500/20 text-green-400 border border-green-500/20"
                  : model.state.tradingSignal === "SELL"
                  ? "bg-red-500/20 text-red-400 border border-red-500/20"
                  : "bg-gray-500/20 text-gray-400 border border-gray-500/20"
              }`}
            >
              {model.state.tradingSignal || "NEUTRAL"}
            </div>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-3 backdrop-blur-sm">
            <div className="text-gray-400 text-sm mb-1">Buy Range</div>
            <div className="font-medium text-green-400">
              {model.state.predictedBuyRange
                ? `₹${model.state.predictedBuyRange.min.toFixed(
                    2
                  )} - ₹${model.state.predictedBuyRange.max.toFixed(2)}`
                : "-"}
            </div>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-3 backdrop-blur-sm">
            <div className="text-gray-400 text-sm mb-1">Sell Range</div>
            <div className="font-medium text-red-400">
              {model.state.predictedSellRange
                ? `₹${model.state.predictedSellRange.min.toFixed(
                    2
                  )} - ₹${model.state.predictedSellRange.max.toFixed(2)}`
                : "-"}
            </div>
          </div>
        </div>

        <div className="mt-4">
          {model.state.isLoading ? (
            <div className="flex items-center justify-center h-[400px] bg-gray-900/30 rounded-xl border border-gray-800">
              <div className="flex flex-col items-center">
                <svg
                  className="animate-spin h-8 w-8 text-blue-500 mb-4"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="text-gray-400">Loading chart data...</span>
              </div>
            </div>
          ) : (
            <>
              {model.state.series[0]?.data?.length > 0 ? (
                <div className="bg-gray-900/30 rounded-xl p-4 border border-gray-800">
                  <ReactApexChart
                    options={{
                      ...model.state.options,
                      chart: {
                        ...model.state.options.chart,
                        background: "transparent",
                        foreColor: "#9ca3af",
                      },
                      grid: {
                        borderColor: "#1f2937",
                      },
                      annotations: showSignals ? {
                        ...model.state.options.annotations,
                        points: model.state.options.annotations?.points?.map(p => ({ ...p, marker: { size: 0 } }))
                      } : { points: [] },
                      theme: {
                        mode: "dark",
                      },
                    }}
                    series={model.state.series}
                    type="candlestick"
                    height={400}
                    width="100%"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[400px] bg-gray-900/30 rounded-xl border border-gray-800">
                  <svg
                    className="w-16 h-16 text-gray-600 mb-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  <p className="text-gray-400 mb-2">No chart data available</p>
                  <p className="text-gray-500 text-sm">
                    Enter a valid stock symbol and click Update Chart
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
